import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { filter } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import cloneDeep from 'lodash-es/cloneDeep';
import { BasketService } from 'src/app/services/basket.service';
import { DataService } from 'src/app/services/data.service';
import { Drink } from 'src/interfaces/drink.model';
import { Units } from 'src/interfaces/enums.model';
import { Modificators } from 'src/interfaces/modificators.model';
import { v4 as uuidv4 } from 'uuid';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { LocalizationService } from 'src/app/services/localization.service';
import { ImageCacheService } from 'src/app/services/image-cache.service';
import { Locale } from 'src/interfaces/locale.model';
import { NO_PHOTO } from 'src/app/utils/no-photo';
import { SheetDirective } from 'src/app/directives/sheet.directive';

@Component({
    selector: 'app-menu-drink-dialog',
    templateUrl: './menu-drink-dialog.component.html',
    styleUrls: ['./menu-drink-dialog.component.css'],
    animations: [
        trigger('slideToggle', [
            state('void', style({ height: '0px', opacity: 0 })),
            state('*', style({ height: '*', opacity: 1 })),
            transition('void <=> *', animate('200ms ease-in-out'))
        ])
    ],
    standalone: false
})
export class MenuDrinkDialogComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    private dialogRef: MatDialogRef<MenuDrinkDialogComponent>,
    private dataService: DataService,
    private basketService: BasketService,
    private localization: LocalizationService,
    private imageCache: ImageCacheService,
    @Inject(MAT_DIALOG_DATA) public data: Drink // Принимаем объект Drink
  ) {
    this.locale = this.dataService.locale;
    this.drink = this.data;
    this.kkal = this.drink.kkal;
    this.original = cloneDeep(this.drink);
    this.isInfoVisible = false;

    // Затемнение и Esc закрывают окно с анимацией, а не мгновенно
    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().subscribe(() => this.close());
    this.dialogRef.keydownEvents()
      .pipe(filter((event) => event.key === 'Escape'))
      .subscribe(() => this.close());
  }

  @ViewChild('scrollContent') scrollContent!: ElementRef;
  @ViewChild('titleOriginal') titleOriginal!: ElementRef;
  @ViewChild(SheetDirective) sheet!: SheetDirective;
  titleOffset: number = 12;

  drink: Drink;
  original: Drink;
  isBasket: boolean = false;
  canUpdate: boolean = false;

  locale: Locale | null = null;

  // Калории
  kkal: number | null;

  // Изображение заглушка
  imageSrc: string = NO_PHOTO.drink;

  /** Нет фото или не загрузилось — векторная заглушка, как в списке */
  get noPhoto(): boolean {
    return this.imageSrc === NO_PHOTO.drink;
  }

  // Модификаторы
  modificators: Modificators | undefined = undefined;

  isInfoVisible: boolean;

  /** Количество и «Добавить» — только со столом (DataService.canOrder) */
  get canOrder(): boolean {
    return this.dataService.canOrder();
  }

  ngOnInit(): void {
    if (this.drink.basketId) {
      this.isBasket = true;
      this.canUpdate = false;
    } else {
      this.drink.count = this.drink.count || 1;
      this.original.count = this.drink.count || 1;
    }

    // Image
    if (this.drink?.image?.trim()) {
      this.imageCache.getImage(this.drink.image, NO_PHOTO.drink).then(src => {
        this.imageSrc = src;
      });
    }

    if (this.drink.variability && this.drink.variants.length > this.drink.choise) {
      console.log('MenuDrinkDialog | ngOnInit() | choise:', this.drink.choise, ' | count:', this.drink.count, ' | price:', this.drink.totalPrice);
      // Цена напитка
      this.drink.price = (this.drink.variants[this.drink.choise].price ?? this.drink.price);
      // Объем напитка
      this.drink.size = (this.drink.variants[this.drink.choise].size ?? this.drink.size);
    }

    // Расчитываем калории на весь напиток, если энергетическая ценность на 100 г/мл
    if (this.drink.energy && this.drink.kkal && (this.drink.unit === Units.weight || this.drink.unit === Units.volume) && this.drink.size) {
      this.kkal = this.drink.size / 100 * this.drink.kkal;
    }

    // Загружаем модификаторы
    const modificators = typeof this.drink.modificators === 'object' ? this.drink.modificators : undefined;
    console.log('MenuDrinkDialog | ngOnInit() | modificators:', modificators);
    if (modificators) {
      this.modificators = modificators;
    }

    this.updatePrice();
  }

  ngAfterViewInit() {
    this.scrollContent.nativeElement.addEventListener('scroll', this.onScroll.bind(this));
    this.titleOffset = this.titleOriginal.nativeElement.offsetTop - 4;
  }

  onScroll(event: any) {
    const scrollOffset = this.scrollContent.nativeElement.scrollTop;
    
    const title = document.querySelector('.drink-dialog-title__header')!
    // console.log('MenuDrinkDialog | onScroll() | scrollOffset:', scrollOffset, ' | titleOffset:', this.titleOffset);
    if (scrollOffset > this.titleOffset) {
      title.classList.add('show');
    } else {
      title.classList.remove('show');
    }
  }

  onModificatorsChange() {
    console.log('MenuDrinkDialog | onModificatorsChange()');
    this.updatePrice();
    this.checkCanUpdateBasket();
  }

  onVariantsChange() {
    console.log('MenuDrinkDialog | onVariantsChange()')
    this.updatePrice();
    
    // Объем
    this.drink.size = (this.drink.variants[this.drink.choise].size ?? this.drink.size);

    // Расчитываем калории на весь напиток, если энергетическая ценность на 100 г/мл
    if (this.drink.energy && this.drink.kkal && (this.drink.unit === Units.weight || this.drink.unit === Units.volume) && this.drink.size) {
      this.kkal = this.drink.size / 100 * this.drink.kkal;
    }
    
    // Проверка корзины, можно обновить или добавить
    this.checkCanUpdateBasket();
  }

  increment(): void {
    this.drink.count++;
    this.checkCanUpdateBasket();
  }

  decrement(): void {
    if (this.drink.count > 1) {
      this.drink.count--;
      this.checkCanUpdateBasket();
    }
  }

  buttonPushRemoveDrinkBasket(): void {
    if (!this.isBasket) {

      // Добавляем блюдо в корзину
      console.log('MenuDrinkDialog | buttonPushRemoveDrinkBasket() | pushDrinkToBasket');
      this.drink.basketId = uuidv4();
      this.basketService.pushDrinkToBasket(this.drink);
    } else {
      if (this.canUpdate) {

        // Обновляем блюдо в корзине
        console.log('MenuDrinkDialog | buttonPushRemoveDrinkBasket() | pushDrinkToBasket');
        this.basketService.updateDrinkInBasket(this.drink);
      } else {

        // Удаляем блюдо из корзины
        console.log('MenuDrinkDialog | buttonPushRemoveDrinkBasket() | removeDrinkDromBasket');
        this.basketService.removeDrinkFromBasket(this.drink);
      }
    } 
    this.close();
  }

  /** Окно уезжает вниз, затем диалог закрывается (onSheetClosed) */
  close(): void {
    this.sheet.dismiss();
  }

  onSheetClosed(): void {
    this.dialogRef.close();
  }

  updatePrice(): void {
    let total = 0;
    // Цена за вариант (если есть)
    if (this.drink.variability && this.drink.variants.length) {
      this.drink.price = this.drink.variants[this.drink.choise].price ?? this.drink.price;
    } 
      
    // Начальная цена за единицу
    total = this.drink.price;

    const modificators = typeof this.drink.modificators === 'object' ? this.drink.modificators : null;
    console.log('MenuDrinkDialog | updatePrice() | modificators:', modificators?.items.map(item => `${item.title}: ${item.count} шт.`));
    modificators?.items.forEach(item => {
      if (item.count != null && item.count > 0) {
        total += item.price * item.count
      }
    });

    this.drink.totalPrice = total;
    console.log('Drink | updatePrice() | count:', this.drink.count, ' | price:', this.drink.totalPrice);
  }

  private checkCanUpdateBasket(): void {
    if (this.isBasket) {

      // Изменился вариант блюда
      if (this.drink.choise != this.original.choise) {
        this.canUpdate = true;
        console.log('MenuDrinkDialog | checkCanUpdateBasket() | canUpdate:', this.canUpdate);
        return
      }

      // Изменилось количество
      if (this.drink.count != this.original.count) {
        this.canUpdate = true;
        console.log('MenuDrinkDialog | checkCanUpdateBasket() | canUpdate:', this.canUpdate);
        return
      }

      // Проверяем, есть ли изменения в модификаторах
      const current = typeof this.drink.modificators === 'object' ? this.drink.modificators : undefined;

      // Модификаторы есть
      if (current && current.items.length > 0) {
        // console.log('MenuDrinkDialog | checkCanUpdateBasket() | Modificator groups:', groups.length);
        
        const modificators = typeof this.original.modificators === 'object' ? this.original.modificators : undefined;
        if (modificators && modificators.items.length > 0) {
          // console.log('MenuDrinkDialog | checkCanUpdateBasket() | modificators:', modificators.items.length, modificatorsCurrent.items.length,);
          
          // Проход по элементам
          for (let i = 0; i < modificators.items.length; i++) {
            
            // Проверяем количество
            if (typeof modificators.items[i] != 'object' || typeof current.items[i] != 'object') {
              this.canUpdate = false;
              return
            }
            if (modificators.items[i].count != current.items[i].count) {
              this.canUpdate = true;
              console.log('MenuDrinkDialog | checkCanUpdateBasket() | canUpdate:', this.canUpdate);
              return
            }
          }
        }
      }
      
      this.canUpdate = false;
      console.log('MenuDrinkDialog | checkCanUpdateBasket() | canUpdate:', this.canUpdate);

    } else {
      this.canUpdate = false;
    }
  }

  selectVariants(index: number) {
    console.log('number: ' + index);
    // this.selectedIndex = index;
  }

  toggleInfo() {
    this.isInfoVisible = !this.isInfoVisible;
  }

  get roundedKkal(): string {
    return this.kkal ? this.kkal.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '';
  }

  get localSize(): string {
    return this.drink.size ? this.drink.size.toLocaleString('ru-RU', { useGrouping: true }) : '';
  }

  // Отписка от событий при уничтожении компонента
  ngOnDestroy() {
    this.scrollContent.nativeElement.removeEventListener('scroll', this.onScroll.bind(this));
  }
}

